// Centralized cache management system
export interface CacheEntry<T> {
  data: T;
  timestamp: number;
  expiresAt: number;
}

export interface CacheOptions {
  ttl?: number; // Time to live in milliseconds
  maxEntries?: number;
}

class CacheManager {
  private static instance: CacheManager;
  private defaultTTL = 5 * 60 * 1000; // 5 minutes
  private maxEntries = 100;

  public static getInstance(): CacheManager {
    if (!CacheManager.instance) {
      CacheManager.instance = new CacheManager();
    }
    return CacheManager.instance;
  }

  // Generic cache operations
  set<T>(key: string, data: T, options: CacheOptions = {}): void {
    const ttl = options.ttl || this.defaultTTL;
    const entry: CacheEntry<T> = {
      data,
      timestamp: Date.now(),
      expiresAt: Date.now() + ttl
    };

    try {
      localStorage.setItem(key, JSON.stringify(entry));
      this.cleanup();
    } catch (error) {
      console.warn('Cache storage failed:', error);
    }
  }

  get<T>(key: string): T | null {
    const result = this.getWithMetadata<T>(key);
    return result ? result.data : null;
  }

  /** Returns cached data with its timestamp (when it was cached). Useful for showing cache age. */
  getWithMetadata<T>(key: string): { data: T; timestamp: number } | null {
    try {
      const stored = localStorage.getItem(key);
      if (!stored) return null;

      const entry: CacheEntry<T> = JSON.parse(stored);

      // Check if expired
      if (Date.now() > entry.expiresAt) {
        localStorage.removeItem(key);
        return null;
      }

      return { data: entry.data, timestamp: entry.timestamp };
    } catch (error) {
      console.warn('Cache retrieval failed:', error);
      localStorage.removeItem(key);
      return null;
    }
  }

  isExpired(key: string): boolean {
    try {
      const stored = localStorage.getItem(key);
      if (!stored) return true;

      const entry: CacheEntry<any> = JSON.parse(stored);
      return Date.now() > entry.expiresAt;
    } catch {
      return true;
    }
  }

  isStale(key: string, staleTime = 60000): boolean {
    try {
      const stored = localStorage.getItem(key);
      if (!stored) return true;

      const entry: CacheEntry<any> = JSON.parse(stored);
      return Date.now() - entry.timestamp > staleTime;
    } catch {
      return true;
    }
  }

  remove(key: string): void {
    localStorage.removeItem(key);
  }

  clear(): void {
    const keys = this.getAllCacheKeys();
    keys.forEach(key => localStorage.removeItem(key));
  }

  // Brand-specific cache operations
  setBrands(userId: string, brands: any[]): void {
    this.set(`brands_${userId}`, brands, { ttl: 10 * 60 * 1000 }); // 10 minutes for brands
  }

  getBrands(userId: string): any[] | null {
    return this.get(`brands_${userId}`);
  }

  setBrandData(brandId: string, data: any): void {
    this.set(`brandData_${brandId}`, data, { ttl: 5 * 60 * 1000 });
  }

  getBrandData(brandId: string): any | null {
    return this.get(`brandData_${brandId}`);
  }

  setRankings(brandId: string, rankings: any[]): void {
    this.set(`rankings_${brandId}`, rankings, { ttl: 3 * 60 * 1000 }); // 3 minutes for rankings
  }

  getRankings(brandId: string): any[] | null {
    return this.get(`rankings_${brandId}`);
  }

  // Content Ideas cache operations
  setContentIdeas(brandId: string, vectorStoreId: string, ideas: any[], selectedIdeas: string[] = []): void {
    const cacheData = {
      ideas,
      selectedIdeas,
      vectorStoreId,
      generatedAt: Date.now()
    };
    this.set(`content_ideas_${brandId}_${vectorStoreId}`, cacheData, { ttl: 30 * 60 * 1000 }); // 30 minutes
  }

  getContentIdeas(brandId: string, vectorStoreId: string): { ideas: any[], selectedIdeas: string[], generatedAt: number } | null {
    return this.get(`content_ideas_${brandId}_${vectorStoreId}`);
  }

  setContentIdeasSelection(brandId: string, vectorStoreId: string, selectedIdeas: string[]): void {
    const existing = this.getContentIdeas(brandId, vectorStoreId);
    if (existing) {
      this.setContentIdeas(brandId, vectorStoreId, existing.ideas, selectedIdeas);
    }
  }

  setVectorStoreSelection(brandId: string, vectorStoreId: string): void {
    this.set(`selected_vector_store_${brandId}`, vectorStoreId, { ttl: 24 * 60 * 60 * 1000 }); // 24 hours
  }

  getVectorStoreSelection(brandId: string): string | null {
    return this.get(`selected_vector_store_${brandId}`);
  }

  // 3D Analysis Cache Methods
  setGSCData(brandId: string, siteUrl: string, data: any): void {
    const cleanSiteUrl = siteUrl.replace(/[^a-zA-Z0-9]/g, '_');
    this.set(`gsc_data_${brandId}_${cleanSiteUrl}`, data, { ttl: 2 * 60 * 60 * 1000 }); // 2 hours TTL
  }

  getGSCData(brandId: string, siteUrl: string): any {
    const cleanSiteUrl = siteUrl.replace(/[^a-zA-Z0-9]/g, '_');
    return this.get(`gsc_data_${brandId}_${cleanSiteUrl}`);
  }

  setEnrichmentData(brandId: string, data: any): void {
    this.set(`enrichment_data_${brandId}`, data, { ttl: 24 * 60 * 60 * 1000 }); // 24 hours TTL
  }

  getEnrichmentData(brandId: string): any {
    return this.get(`enrichment_data_${brandId}`);
  }

  set3DAnalysisUIState(brandId: string, state: any): void {
    this.set(`ui_state_3d_${brandId}`, state, { ttl: 7 * 24 * 60 * 60 * 1000 }); // 7 days TTL
  }

  get3DAnalysisUIState(brandId: string): any {
    return this.get(`ui_state_3d_${brandId}`);
  }

  setStoredDataStatus(brandId: string, status: any): void {
    this.set(`stored_data_status_${brandId}`, status, { ttl: 30 * 60 * 1000 }); // 30 minutes TTL
  }

  getStoredDataStatus(brandId: string): any {
    return this.get(`stored_data_status_${brandId}`);
  }

  // Cache invalidation
  invalidateBrandCache(brandId: string): void {
    this.remove(`brandData_${brandId}`);
    this.remove(`rankings_${brandId}`);
  }

  invalidateUserCache(userId: string): void {
    this.remove(`brands_${userId}`);
  }

  invalidateContentIdeasCache(brandId: string, vectorStoreId?: string): void {
    if (vectorStoreId) {
      this.remove(`content_ideas_${brandId}_${vectorStoreId}`);
    } else {
      // Clear all content ideas for this brand
      const keys = this.getAllCacheKeys();
      keys.forEach(key => {
        if (key.startsWith(`content_ideas_${brandId}_`)) {
          this.remove(key);
        }
      });
    }
  }

  invalidate3DAnalysisCache(brandId: string): void {
    // Remove all 3D Analysis related cache for this brand
    const keys = this.getAllCacheKeys();
    const keysToRemove = keys.filter(key => 
      key.includes(`gsc_data_${brandId}_`) ||
      key === `enrichment_data_${brandId}` ||
      key === `ui_state_3d_${brandId}` ||
      key === `stored_data_status_${brandId}`
    );
    
    keysToRemove.forEach(key => this.remove(key));
    console.log('Invalidated 3D Analysis cache for brand:', brandId, 'removed keys:', keysToRemove.length);
  }

  // Cleanup old entries
  private cleanup(): void {
    try {
      const keys = this.getAllCacheKeys();
      if (keys.length <= this.maxEntries) return;

      // Sort by timestamp, remove oldest
      const entries = keys
        .map(key => {
          try {
            const stored = localStorage.getItem(key);
            if (!stored) return null;
            const entry = JSON.parse(stored);
            return { key, timestamp: entry.timestamp };
          } catch {
            return null;
          }
        })
        .filter(Boolean)
        .sort((a, b) => a!.timestamp - b!.timestamp);

      const toRemove = entries.slice(0, entries.length - this.maxEntries);
      toRemove.forEach(entry => entry && localStorage.removeItem(entry.key));
    } catch (error) {
      console.warn('Cache cleanup failed:', error);
    }
  }

  private getAllCacheKeys(): string[] {
    const keys: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && (
        key.startsWith('brands_') ||
        key.startsWith('brandData_') ||
        key.startsWith('rankings_') ||
        key.startsWith('content_ideas_') ||
        key.startsWith('selected_vector_store_') ||
        key.startsWith('gsc_data_') ||
        key.startsWith('enrichment_data_') ||
        key.startsWith('ui_state_3d_') ||
        key.startsWith('stored_data_status_') ||
        key.startsWith('dashboard-config-') ||
        key.startsWith('main-dashboard-') ||
        key.startsWith('main-saio-dashboard-')
      )) {
        keys.push(key);
      }
    }
    return keys;
  }
}

export const cache = CacheManager.getInstance();